import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import wordcloud from "highcharts/modules/wordcloud";
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import fullscreen from "highcharts/modules/full-screen";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import ChartDatadrawer from "../ChartDatadrawer";
import GridLight from "highcharts/themes/grid-light";
import ChartFallbackImg from "./ChartFallbackImg";

// Initialize Highcharts modules
GridLight(Highcharts);
wordcloud(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

const WordCloudChart = ({ wordcloudData }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) {
          chart.reflow();
        }
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (
    !wordcloudData ||
    typeof wordcloudData !== "object" ||
    Array.isArray(wordcloudData) ||
    Object.keys(wordcloudData).length === 0
  ) {
    return <ChartFallbackImg titleText="Frequently Used Keywords" />;
  }

  const sortedData = Object.entries(wordcloudData)
    .map(([key, value]) => ({
      name: key,
      weight: value,
    }))
    .sort((a, b) => b.weight - a.weight);

  const calculateDynamicThreshold = (data) => {
    if (data.length <= 50) return 1;

    const weights = data.map((item) => item.weight);
    const mean = weights.reduce((sum, val) => sum + val, 0) / weights.length;
    const stdDev = Math.sqrt(
      weights.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        weights.length
    );

    return data.length > 200 ? mean - 0.5 * stdDev : Math.max(2, mean - stdDev);
  };

  const calculateOptimalWordCount = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportArea = viewportWidth * viewportHeight;
    const baseCount = Math.floor(viewportArea / 10000);
    return Math.min(Math.max(30, baseCount), 150);
  };

  const threshold = calculateDynamicThreshold(sortedData);
  const optimalWordCount = calculateOptimalWordCount();

  // Refined color scheme - maintaining the blue theme but with better distinction
  const colorScheme = [
    // Original colors
    "#8576FF", // Purple-blue
    "#7900FF", // Deep purple
    "#4FA4FF", // Bright blue
    "#00E5B0", // Teal
    "#1679AB", // Steel blue
    "#45CFB6", // Seafoam
    "#00D9A6", // Mint
    "#4B9FE1", // Sky blue
    "#2D7FFF", // Royal blue
    // New complementary colors
    "#6C63FF", // Periwinkle
    "#9D8DF1", // Lavender
    "#34B3F1", // Ocean blue
    "#00B8A9", // Turquoise
    "#5E60CE", // Deep periwinkle
    "#48BFE3", // Light azure
    "#80FFDB", // Aquamarine
    "#64DFDF", // Cyan
    "#5390D9", // Cornflower blue
    "#7400B8", // Royal purple
    "#5E60CE", // Indigo
    "#4EA8DE", // Steel blue
    "#56CFE1", // Sky blue
  ];

  const transformedData = sortedData
    .filter((item) => item.weight >= threshold)
    .slice(0, optimalWordCount)
    .map((item, index) => ({
      ...item,
      color: colorScheme[index % colorScheme.length],
    }));

  const handleWordClick = (event) => {
    const keyWord = event.point.name;
    setSelectedKeyword(keyWord);
    setIsDrawerOpen(true);
  };

  const options = {
    chart: {
      type: "wordcloud",
      backgroundColor: "#ffffff",
      events: {
        load: function () {
          this.reflow();
        },
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: `Frequently Used Keywords (${transformedData.length} most relevant terms)`,
    },
    tooltip: {
      pointFormat:
        '<span style="color:{point.color}">●</span> <h1>{point.weight}</h1><br/>',
      style: {
        fontSize: "20px",
        fontWeight: "bold",
      },
    },
    plotOptions: {
      series: {
        cursor: "pointer",
        point: {
          events: {
            click: handleWordClick,
          },
        },
      },
      wordcloud: {
        minFontSize: 20,
        maxFontSize: 48,
        rotation: {
          from: -30,
          to: 30,
          orientations: 5,
        },
      },
    },
    series: [
      {
        type: "wordcloud",
        data: transformedData,
        rotation: {
          from: 0,
          to: 0,
        },
        colorByPoint: true,
      },
    ],
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            "viewData",
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
          ],
        },
      },
      csv: {
        columnHeaderFormatter: (item, key) => {
          if (!item) {
            return key === "name" ? "Keyword" : "Count";
          }
          return false;
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            title: {
              style: {
                fontSize: "16px",
              },
            },
            series: [
              {
                dataLabels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            ],
          },
        },
      ],
    },
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          // padding: "20px",
          // borderRadius: "8px",
          // boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          background: "white",
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <SwipeableDrawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onOpen={() => setIsDrawerOpen(true)} // 🔧 Add this line
        PaperProps={{
          sx: {
            height: "100%",
            overflow: "hidden",
            borderTopLeftRadius: "12px",
            borderBottomLeftRadius: "12px",
          },
        }}
      >
        <Box
          sx={{
            width: 650,
            padding: 2,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {selectedKeyword && <ChartDatadrawer keyword={selectedKeyword} />}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default WordCloudChart;
